// The live channel, and the poll it falls back to. P5-3 T5.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 WHY THIS IS `fetch` AND NOT `EventSource`
// ═══════════════════════════════════════════════════════════════════════════
//
// Two constraints, both from the spec, and together they rule out the obvious
// implementation:
//
//   §3 — the live channel is a **direct browser↔service connection**, because
//        "long-lived connections through Pages Functions is the quiet limit
//        this design refuses to discover in prod."
//   §7 — of the three tokens, "**none is ever in a URL**, a log line, or the
//        repo."
//
// `EventSource` cannot set a request header. The only way to authenticate one
// is `?token=…`, which §7 forbids in as many words — and which would also put
// the credential in Railway's HTTP logs and in any `Referer` the page emits.
//
// `fetch` can set `Authorization`, keeps the connection direct, and satisfies
// both. What it costs is `EventSource`'s automatic reconnect, which this file
// therefore owns. That cost is smaller than it looks: the fallback logic below
// has to exist regardless, and a reconnect is the same code path as engaging
// the fallback and then recovering from it.
//
// The third option — proxying the stream through a Pages Function so the
// session cookie authenticates it — is the one §3 rejects by name. It was not
// re-argued here.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 A CHANGE EVENT MEANS "RE-READ THE BOARD". IT IS NOT A PATCH.
// ═══════════════════════════════════════════════════════════════════════════
//
// The feed carries what changed, and this file deliberately does not apply it.
// T4 established why, for the member's own writes: a write moves the estimate
// — coverage, the per-currency subtotals, which flat candidate the roll-up
// used — and none of that is derivable in the browser without reimplementing
// cost.py. Patching one card and leaving the figures stale is how a board ends
// up showing "amounts on 8 of 12" beside thirteen cards.
//
// Somebody else's write is the same write. So a change event is a trigger, and
// the board read is the truth.
//
// Three things fall out, and they are why this design is worth the paragraph:
//
//   1. **The tail and the fallback become identical in effect.** Both produce
//      "something changed, re-read". §5.6's *"same data, second transport,
//      zero new semantics"* is not a promise this file has to keep — it has
//      nothing to keep it with.
//   2. **Over-delivery is free.** A change replayed twice is a redundant
//      refresh. That is what lets the board Function take its cursor BEFORE
//      the board content (see that file) — the cheap fix for the only race in
//      this pairing.
//   3. **Ordering stops mattering.** The board read is a whole consistent
//      answer, so a burst of five changes coalesces into one refresh and the
//      result is the same as applying them in order.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IT HAS NO ERROR CHANNEL, BY CONSTRUCTION
// ═══════════════════════════════════════════════════════════════════════════
//
// §8: *"Live channel drops → client falls back to since-cursor polling
// (automatic) and says nothing unless asked — reconnect is not the user's
// problem."*
//
// `openLiveChannel` takes ONE callback and it is `onChange`. There is no
// `onError`, no status, no "reconnecting" state for a component to render,
// because a caller cannot render something it is never handed. A console line
// on an unplanned transition is the whole of the telemetry, and it names the
// transition and nothing else — never a token, never a URL.

/** A parsed SSE frame. `data` is null for a comment or a bare `retry:`. */
function parseFrame(raw) {
  const frame = { event: null, id: null, data: null, comment: null }
  const dataLines = []
  for (const line of raw.split('\n')) {
    if (line === '') continue
    if (line.startsWith(':')) {
      frame.comment = line.slice(1).replace(/^ /, '')
      continue
    }
    const colon = line.indexOf(':')
    const name = colon === -1 ? line : line.slice(0, colon)
    let value = colon === -1 ? '' : line.slice(colon + 1)
    if (value.startsWith(' ')) value = value.slice(1)
    if (name === 'data') dataLines.push(value)
    else if (name === 'event') frame.event = value
    else if (name === 'id') frame.id = value
    // `retry:` and anything unrecognised are ignored, per the SSE spec. This
    // client owns its own reconnect, so `retry:` is genuinely nothing to us.
  }
  if (dataLines.length > 0) frame.data = dataLines.join('\n')
  return frame
}

/**
 * Incremental SSE parser. Feed it decoded text; get complete frames back.
 *
 * 🔴 IT NORMALISES LINE ENDINGS, AND THE HELD-BACK `\r` IS NOT PARANOIA.
 * The SSE wire format separates lines with CRLF, LF **or** a bare CR, and our
 * server writes LF — but the bytes between here and it pass through Railway's
 * edge and whatever else is on the path. A parser that only knew `\n\n` would
 * work in every test and stall on a proxy that normalised to CRLF, which is
 * the CLAUDE.md gotcha 23 family (the same file scanning differently on either
 * side of a line-ending boundary).
 *
 * The subtle half: a chunk can END on the `\r` of a `\r\n`. Normalising it to
 * `\n` right then, and then seeing the next chunk's `\n`, invents a frame
 * boundary that is not there — splitting one event into two malformed ones.
 * So a trailing `\r` is held back until the next chunk arrives.
 */
export class SseDecoder {
  constructor() {
    this.buffer = ''
  }

  push(text) {
    this.buffer += text
    let held = ''
    if (this.buffer.endsWith('\r')) {
      held = '\r'
      this.buffer = this.buffer.slice(0, -1)
    }
    const normalised = this.buffer.replaceAll('\r\n', '\n').replaceAll('\r', '\n')
    const parts = normalised.split('\n\n')
    this.buffer = parts.pop() + held
    return parts.map(parseFrame)
  }
}

/** How long a silent tail is allowed to stay open.
 *
 * The service beats every 15 seconds. Three missed beats is dead: a TCP
 * connection that has been reaped, a proxy that is holding our bytes, a laptop
 * that slept. Without this the failure mode is the worst one available — a
 * board that looks live and is not, which is exactly what the fallback exists
 * to prevent and would never engage. */
export const SILENCE_LIMIT_MS = 45_000

/** The fallback's cadence.
 *
 * Not one second. The tail is the live path; this is the safety net, and six
 * browsers asking a Function every second is a real cost for a transport that
 * is only running because something is already wrong. T2's ruling is
 * "live-ish", and the fallback is explicitly the pre-authorised degradation. */
export const POLL_INTERVAL_MS = 5_000

/** How long to wait before trying the tail again after it failed. Doubles up
 * to the ceiling, so a service that is down is not hammered, and a blip
 * recovers in a couple of seconds. */
export const TAIL_RETRY_MS = 2_000
export const TAIL_RETRY_CEILING_MS = 60_000

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Watch a trip's changes feed. Returns `{ close }`.
 *
 * @param {object}   options
 * @param {string}   options.tripId
 * @param {number}   options.cursor    from the board payload — `0` is valid
 * @param {Function} options.onChange  called with `{ changes, via }`
 */
export function openLiveChannel({
  tripId,
  cursor,
  onChange,
  fetchImpl,
  silenceLimitMs = SILENCE_LIMIT_MS,
  pollIntervalMs = POLL_INTERVAL_MS,
  tailRetryMs = TAIL_RETRY_MS,
  tailRetryCeilingMs = TAIL_RETRY_CEILING_MS,
  log = (message) => console.info(message),
} = {}) {
  const doFetch = fetchImpl ?? ((...args) => globalThis.fetch(...args))
  const encodedTrip = encodeURIComponent(tripId)

  // 🔴 `Number.isInteger`, not `cursor || 0`. A cursor of 0 is the honest
  // answer for a trip whose feed is empty, and `||` would turn it into 0 as
  // well — right by accident today, and wrong the moment the fallback value
  // stops being 0.
  let at = Number.isInteger(cursor) ? cursor : 0
  let stopped = false
  let controller = null

  function deliver(changes, via) {
    if (stopped || changes.length === 0) return
    // The cursor only ever moves forward. The two transports can overlap
    // during a handover, so an older reply must not rewind it and cause the
    // same rows to be replayed forever.
    for (const change of changes) {
      if (Number.isInteger(change?.seq) && change.seq > at) at = change.seq
    }
    onChange?.({ changes, via })
  }

  /** One attempt at the tail. Resolves when the stream ends; throws on any
   * failure, which the caller reads as "fall back". */
  async function tail(onOpen) {
    const minted = await doFetch(`/api/trips/${encodedTrip}/channel`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!minted.ok) throw new Error(`channel mint refused: ${minted.status}`)
    const { token, stream } = await minted.json()
    if (typeof token !== 'string' || typeof stream !== 'string') {
      throw new Error('channel mint answered without a token')
    }

    controller = new AbortController()
    // 🔴 The credential is a HEADER. Never `?token=` — §7, and a query string
    // reaches Railway's HTTP logs and any Referer the page sends.
    // `credentials: 'omit'` because this is cross-origin and the session
    // cookie has no business here; it also keeps `Access-Control-Allow-Origin:
    // *` on the service side honest (see trips/utils/channel.py).
    const response = await doFetch(`${stream}?since=${at}`, {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store',
      headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
      signal: controller.signal,
    })
    if (!response.ok || !response.body) {
      throw new Error(`tail refused: ${response.status}`)
    }
    onOpen?.()

    const reader = response.body.getReader()
    // 🔴 `{ stream: true }`. Without it a multi-byte character split across
    // two chunks decodes as two replacement characters — a title with an
    // accent in it silently corrupted, on a boundary nothing controls.
    const decoder = new TextDecoder('utf-8')
    const sse = new SseDecoder()

    let silence = setTimeout(() => controller?.abort(), silenceLimitMs)
    const resetSilence = () => {
      clearTimeout(silence)
      silence = setTimeout(() => controller?.abort(), silenceLimitMs)
    }

    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (done) return
        resetSilence()
        const batch = []
        for (const frame of sse.push(decoder.decode(value, { stream: true }))) {
          if (frame.event === 'change' && frame.data !== null) {
            try {
              batch.push(JSON.parse(frame.data))
            } catch {
              // A frame we cannot parse is one event lost, not a reason to
              // tear the channel down — and the poll would re-deliver it on
              // the next handover anyway, because `at` did not advance past it.
            }
          }
        }
        deliver(batch, 'tail')
        if (stopped) return
      }
    } finally {
      clearTimeout(silence)
      try {
        await reader.cancel()
      } catch {
        // The stream is already gone. Nothing to release.
      }
    }
  }

  /** One poll. Returns true if it worked. */
  async function pollOnce() {
    const response = await doFetch(`/api/trips/${encodedTrip}/changes?since=${at}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) return false
    const body = await response.json()
    deliver(Array.isArray(body?.changes) ? body.changes : [], 'poll')
    // The service's cursor is authoritative for an empty page too — it is how
    // a client that has been away catches up past rows it does not need.
    if (Number.isInteger(body?.cursor) && body.cursor > at) at = body.cursor
    return true
  }

  /** The whole lifecycle. One loop, two transports, no state machine.
   *
   * While the tail is up, this function is parked inside `tail()`. When the
   * tail ends cleanly — which happens every ten minutes, because the token
   * expires — it re-mints at once. When it fails, the loop polls at
   * `pollIntervalMs` and retries the tail on a doubling backoff, so a service
   * that is down is not hammered and a blip recovers in a couple of seconds.
   *
   * 🔴 THE FIRST POLL IS IMMEDIATE. Whatever killed the tail may have happened
   * while somebody else was editing, so waiting a poll interval before the
   * first fallback read would leave the board stale for exactly as long as it
   * takes somebody to notice — which is the ceremony §8 says not to have.
   *
   * 🔴 AND A TAIL THAT ENDS INSTANTLY MUST NOT BECOME A HOT LOOP. A stream
   * that opens and closes with no error is not a failure by any test in here,
   * and re-minting on it without a floor would be an unbounded request loop
   * against our own Function. `lasted` is the floor.
   */
  async function run() {
    let backoff = tailRetryMs
    let degraded = false
    while (!stopped) {
      const openedAt = Date.now()
      let failed = false
      try {
        await tail(() => {
          if (degraded) {
            log('trips: live channel back')
            degraded = false
          }
          backoff = tailRetryMs
        })
      } catch {
        failed = true
      }
      if (stopped) return
      const lasted = Date.now() - openedAt

      if (!failed) {
        if (lasted < tailRetryMs) await sleep(tailRetryMs)
        continue
      }

      if (!degraded) {
        // Said once per outage rather than once per attempt. It names the
        // transition and carries nothing else — no token, no URL, no status.
        log('trips: live channel unavailable, polling instead')
        degraded = true
      }

      const until = Date.now() + backoff
      do {
        try {
          await pollOnce()
        } catch {
          // Offline, most likely. The board keeps showing what it has (§8)
          // and the next turn of this loop tries again.
        }
        if (stopped) return
        await sleep(pollIntervalMs)
      } while (!stopped && Date.now() < until)
      backoff = Math.min(backoff * 2, tailRetryCeilingMs)
    }
  }

  // Never awaited, and never allowed to reject: an unhandled rejection in a
  // React effect is a console error the member can see, which is ceremony.
  run().catch(() => {})

  return {
    close() {
      stopped = true
      controller?.abort()
    },
    /** For tests and for nothing else: where the cursor has reached. */
    get cursor() {
      return at
    },
  }
}
